import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ABSOLUTE_URLS } from '../../../constants';
import { GET_MEMBERSHIP_COSTS } from '../../../features/Reactivate/graphql/queries/GET_MEMBERSHIP_COSTS';
import {
  GQLCountry,
  GQLProgram,
  GQLQuery,
  GQLSubscriptionStatus,
  GQLUserSubscription,
  Maybe,
} from '../../../gql.generated';
import { logAnalyticsEvent } from '../../../utils/analytics';
import { AnalyticsEventKey } from '../../../utils/analytics/constants';
import { GBX_UPGRADE_CHECKOUT_ANALYTICS_LABELS } from '../constants';
import useGetOrganicCheckoutPage, {
  ExtendedLayout,
} from '../graphql/hooks/useGetOrganicCheckoutPage';
import { getExperiment } from '../utils';

const analyticsKey: AnalyticsEventKey = 'GLAMBAG_X_CONDENSED';

export const getShippingCountry = (
  subscriptions: Maybe<Maybe<GQLUserSubscription>[]> | undefined
) => {
  let country = null;
  const activeFullsize = subscriptions?.find(
    (sub) =>
      sub?.program === GQLProgram.FULLSIZE &&
      sub?.status === GQLSubscriptionStatus.ACTIVE
  );
  if (activeFullsize) {
    country = activeFullsize?.shippingAddress?.country;
  } else {
    const glambag = subscriptions?.find(
      (sub) => sub?.program === GQLProgram.GLAMBAG
    );
    if (glambag) {
      country = glambag?.shippingAddress?.country;
    }
  }

  return country;
};

const useUpgradeGbxCheckout = () => {
  const router = useRouter();
  const initialRender = useRef(true);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const { de, wg } = router.query as { de?: string; wg?: string };

  const experimentId = getExperiment(de?.toLowerCase());
  const isWithGift =
    wg === 'true' || wg === 'false' ? wg === 'true' : undefined;

  const {
    data,
    loading: loadingQuery,
    error: queryError,
  } = useGetOrganicCheckoutPage(experimentId, experimentId?.toString());

  const shippingCountry = getShippingCountry(data?.me?.subscriptions);
  const { data: membershipCostsData } = useQuery<GQLQuery>(
    GET_MEMBERSHIP_COSTS,
    {
      variables: {
        program: GQLProgram.GBX,
        country: shippingCountry as GQLCountry,
        period: 1,
      },
    }
  );

  const layout = data?.me?.layout as
    | (ExtendedLayout & {
        paymentRetrialEnabled: string;
      })
    | undefined;

  const paymentRetrialEnabled =
    layout?.paymentRetrialEnabled?.toUpperCase() === 'ON';

  const shippingCost =
    membershipCostsData?.me?.membershipCosts?.shippingCost?.amount;

  const checkoutPage = {
    ...layout?.subscriptionCheckoutAsset,
    ...layout?.gbxOrganicUpgradePage?.checkoutPage,
    info: {
      header: layout?.subscriptionCheckoutAsset?.checkoutinfoheader,
      bulletPoints: [
        layout?.subscriptionCheckoutAsset?.checkoutinfopointone,
        layout?.subscriptionCheckoutAsset?.checkoutinfopointtwo,
      ],
    },
    checkoutnewannualupgradeprice:
      layout?.subscriptionCheckoutAsset?.checkoutnewannualupgradeprice?.replace(
        '${shippingCost}',
        `$${shippingCost}`
      ),
  };

  const { paymentError, paymentErrorMsg } = checkoutPage || {};
  const isNewMembershipPageInUse =
    layout?.useNewMembershipConfirmationPage ?? true;

  const logAnalytics = useCallback((label: string) => {
    logAnalyticsEvent(
      {
        name: analyticsKey,
        label,
      },
      null,
      false
    );
  }, []);

  useEffect(() => {
    if (initialRender.current) {
      if (!checkoutPage && !loadingQuery) {
        router.push(ABSOLUTE_URLS.GLAM_BAG);
      } else {
        logAnalytics(GBX_UPGRADE_CHECKOUT_ANALYTICS_LABELS.VIEWED);
        initialRender.current = false;
        setLoading(false);
      }
    }
  }, [logAnalytics, checkoutPage, loadingQuery, router]);

  const onExitRedirect = () => {
    router.push(ABSOLUTE_URLS.GLAM_BAG);
  };

  const toggleModal = (): void => {
    logAnalytics(
      showModal
        ? GBX_UPGRADE_CHECKOUT_ANALYTICS_LABELS.CLOSED_LEARN_MORE
        : GBX_UPGRADE_CHECKOUT_ANALYTICS_LABELS.CLICKED_LEARN_MORE
    );
    setShowModal((show) => !show);
  };

  const onCrossClick = async (
    event?: React.MouseEvent<Element, MouseEvent>
  ): Promise<void> => {
    event?.preventDefault();
    logAnalytics(GBX_UPGRADE_CHECKOUT_ANALYTICS_LABELS.CLICKED_CROSS);
    onExitRedirect();
  };

  const onLogoClick = async (
    event?: React.MouseEvent<Element, MouseEvent>
  ): Promise<void> => {
    event?.preventDefault();
    logAnalytics(GBX_UPGRADE_CHECKOUT_ANALYTICS_LABELS.CLICKED_IPSY_LOGO);
    router.push(ABSOLUTE_URLS.GLAM_BAG);
  };

  return {
    checkoutPage,
    isNewMembershipPageInUse,
    onCrossClick,
    onLogoClick,
    logAnalytics,
    toggleModal,
    showModal,
    loading: loading || loadingQuery,
    queryError,
    paymentError,
    paymentErrorMsg,
    experimentId,
    isWithGift,
    paymentRetrialEnabled,
    gbxMetaTitle: data?.me?.layout?.gbxMetaTitle?.value,
    gbxMetaDescription: data?.me?.layout?.gbxMetaDescription?.value,
  };
};

export default useUpgradeGbxCheckout;
